import { useState, useEffect } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { LogIn, UserPlus, Eye, EyeOff, CheckCircle, X } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { supabase } from '../lib/supabase'
import { TeamType } from '../../types'

const teams: TeamType[] = [
  '1. Mannschaft', '2. Mannschaft', '3. Mannschaft',
  '50+', '40+', '30+',
  'BA', 'BB', 'CA', 'CB', 'FF-14', 'FF-17',
  'D9A', 'D9B', 'D9C', 'D7A', 'D7B', 'D7C',
  'EA', 'EB', 'EC', 'ED', 'FA', 'FB', 'G'
]

export default function LoginPage() {
  const { signIn, signUp, isAuthenticated, loading } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    team: '1. Mannschaft' as TeamType,
    role: 'trainer' as 'admin' | 'trainer'
  })
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isResetting, setIsResetting] = useState(false)

  // Passwort vergessen: nutzt die E-Mail, die im Formular schon steht.
  // Die Antwort ist bewusst immer gleich, egal ob das Konto existiert —
  // sonst liesse sich hier durchprobieren, wer Mitglied ist.
  const handleForgotPassword = async () => {
    const email = formData.email.trim()
    if (!email) {
      setSuccessMessage('')
      setError('Bitte zuerst die E-Mail-Adresse eintragen.')
      return
    }

    setError('')
    setSuccessMessage('')
    setIsResetting(true)
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    setIsResetting(false)

    if (resetError) {
      console.error('Passwort-Reset fehlgeschlagen:', resetError)
    }
    setSuccessMessage(
      `Falls für ${email} ein Konto besteht, wurde eine E-Mail zum Zurücksetzen verschickt. Bitte auch den Spam-Ordner prüfen.`
    )
  }

  // Check for registration success message
  useEffect(() => {
    const email = searchParams.get('email')
    const registered = searchParams.get('registered')
    
    if (registered === 'true' && email) {
      setSuccessMessage(`Registrierung erfolgreich! E-Mail an ${email} gesendet.`)
      setFormData(prev => ({ ...prev, email }))
      setIsLogin(true)
      // Clear URL parameters
      setSearchParams({})
    }
  }, [searchParams, setSearchParams])

  // Handle auth state changes to prevent redirect loops
  useEffect(() => {
    console.log('🔄 Setting up auth state change listener in LoginPage')
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('LoginPage - Auth event received:', {
          event,
          hasSession: !!session,
          userEmail: session?.user?.email,
          timestamp: new Date().toISOString()
        })
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('LoginPage - Successful sign in detected, checking for redirect', {
            currentPath: window.location.pathname,
            userEmail: session.user.email
          })
          
          // Avoid redirect loops by checking current location
          if (window.location.pathname === '/login') {
            console.log('✅ LoginPage - Redirecting to home after successful sign in')
            navigate('/', { replace: true })
          } else {
            console.log('⏭️ LoginPage - Already on correct page, no redirect needed')
          }
        }
        
        if (event === 'SIGNED_OUT') {
          console.log('🧹 LoginPage - Sign out detected, clearing storage')
          localStorage.clear()
        }
      }
    )

    return () => {
      console.log('🔄 Cleaning up auth state change listener in LoginPage')
      subscription.unsubscribe()
    }
  }, [navigate])

  // Debug logging with storage health check
  console.log('LoginPage render:', {
    loading,
    isAuthenticated: isAuthenticated(),
    currentPath: window.location.pathname,
    timestamp: new Date().toISOString()
  })
  
  // Add storage health check for debugging
  const storageKeys = Object.keys(localStorage).filter(key => key.includes('supabase') || key.includes('auth'))
  if (storageKeys.length > 0) {
    console.log('LoginPage - Auth storage keys found:', storageKeys.map(key => ({
      key,
      hasValue: !!localStorage.getItem(key)
    })))
  } else {
    console.log('LoginPage - No auth storage keys found')
  }

  // FIXED: Redirect wenn bereits angemeldet (auch während loading wenn user bereits da ist)
  if (isAuthenticated()) {
    console.log('LoginPage - User is already authenticated, redirecting to home:', {
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString()
    })
    return <Navigate to="/" replace />
  }
  
  // Zeige Loading während Auth-Initialisierung (nur wenn nicht authentifiziert)
  if (loading) {
    console.log('LoginPage - Showing loading screen (auth initialization in progress)')
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #1B7E3A',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p>Lädt...</p>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '8px'
          }}>Authentifizierung wird überprüft...</p>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    console.log(`🔑 ${isLogin ? 'LOGIN' : 'SIGNUP'} attempt started:`, {
      email: formData.email,
      hasPassword: !!formData.password,
      isLogin,
      timestamp: new Date().toISOString()
    })

    try {
      if (isLogin) {
        console.log('🔐 Attempting sign in...')
        const result = await signIn(formData.email, formData.password)
        
        console.log('🔐 Sign in result:', {
          success: !result.error,
          error: result.error,
          timestamp: new Date().toISOString()
        })
        
        if (result.error) {
          console.error('❌ Sign in failed:', result.error)
          setError(result.error)
        } else {
          console.log('✅ Sign in successful - auth listener should handle redirect')
        }
      } else {
        console.log('🔐 Starting registration process...', {
          email: formData.email,
          name: formData.name,
          team: formData.team,
          role: formData.role
        })
        
        const result = await signUp(formData.email, formData.password, {
          name: formData.name,
          team: formData.team,
          role: formData.role
        })
        
        console.log('📧 Signup result received:', {
          success: !result.error,
          error: result.error,
          timestamp: new Date().toISOString()
        })
        
        if (result.error) {
          console.error('❌ Signup failed:', result.error)
          // Check if this is actually a success message disguised as an error
          if (result.error.includes('Registrierung erfolgreich')) {
            setSuccessMessage(result.error)
            setError('')
            setIsLogin(true) // Switch to login mode
          } else {
            setError(result.error)
          }
        } else {
          console.log('✅ Registration completed successfully')
          setSuccessMessage('Registrierung erfolgreich! Wenn E-Mail-Bestätigung aktiviert ist, überprüfen Sie bitte Ihre E-Mails. Ihr Account wird automatisch erstellt und muss anschließend von einem Administrator genehmigt werden.')
          setError('')
          setIsLogin(true) // Switch to login mode
        }
      }
    } catch (err: any) {
      console.error('❌ Form submission error:', {
        error: err.message,
        stack: err.stack,
        isLogin,
        timestamp: new Date().toISOString()
      })
      setError(err.message || 'Ein unerwarteter Fehler ist aufgetreten')
    } finally {
      console.log('🏁 Form submission completed, setting isSubmitting to false')
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError('')
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #1B7E3A',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6B7280', fontWeight: '500' }}>Lädt...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 50%, #f0fdf4 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 16px'
    }}>
      <div style={{ maxWidth: '448px', width: '100%' }}>
        {/* Header Section with FC Frick Branding */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            padding: '32px',
            marginBottom: '24px',
            border: '1px solid #dcfce7'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #1B7E3A 0%, #16a34a 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 15px -3px rgba(27, 126, 58, 0.3)'
                }}>
                  <img 
                    src="/small-fc-frick-logo.png" 
                    alt="FC Frick Logo" 
                    style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                  />
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '-8px',
                  width: '32px',
                  height: '32px',
                  background: '#fbbf24',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ color: '#166534', fontWeight: 'bold', fontSize: '14px' }}>⚽</span>
                </div>
              </div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #1B7E3A 0%, #16a34a 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.025em'
              }}>
                FC Frick
              </h1>
              <p style={{
                color: '#16a34a',
                fontWeight: '600',
                fontSize: '14px',
                marginTop: '4px'
              }}>
                Trainer Portal
              </p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '8px'
            }}>
              {isLogin ? 'Willkommen zurück' : 'Konto erstellen'}
            </h2>
            <p style={{ color: '#6b7280' }}>
              {isLogin 
                ? 'Melden Sie sich mit Ihrem Trainer-Account an' 
                : 'Erstellen Sie ein neues Trainer-Konto für FC Frick'
              }
            </p>
          </div>
        </div>

        {/* Main Form Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid #dcfce7',
          overflow: 'hidden'
        }}>
          {/* Success Message */}
          {successMessage && (
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
              borderBottom: '1px solid #bbf7d0',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#dcfce7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '2px'
                }}>
                  <CheckCircle style={{ width: '16px', height: '16px', color: '#16a34a' }} />
                </div>
                <div style={{ marginLeft: '12px' }}>
                  <p style={{ fontWeight: '600', color: '#166534' }}>{successMessage}</p>
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: '#15803d' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '6px', height: '6px', background: '#22c55e', borderRadius: '50%', marginRight: '8px' }}></div>
                      <span>Bitte bestätigen Sie Ihre E-Mail-Adresse</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '6px', height: '6px', background: '#22c55e', borderRadius: '50%', marginRight: '8px' }}></div>
                      <span>Ihr Account muss von einem Administrator genehmigt werden</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              borderBottom: '1px solid #fecaca',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <X style={{ width: '16px', height: '16px', color: '#dc2626' }} />
                </div>
                <p style={{ marginLeft: '12px', color: '#991b1b', fontWeight: '600' }}>{error}</p>
              </div>
            </div>
          )}

          {/* Form Content */}
          <form style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }} onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Email Field */}
              <div>
                <label htmlFor="email" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  E-Mail-Adresse
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    fontSize: '16px',
                    background: '#f9fafb',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.background = 'white'
                    e.target.style.borderColor = '#16a34a'
                    e.target.style.boxShadow = '0 0 0 3px rgba(22, 163, 74, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.background = '#f9fafb'
                    e.target.style.borderColor = '#d1d5db'
                    e.target.style.boxShadow = 'none'
                  }}
                  placeholder="trainer@fcfrick.ch"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Passwort
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '12px 48px 12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: '#f9fafb',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.background = 'white'
                      e.target.style.borderColor = '#16a34a'
                      e.target.style.boxShadow = '0 0 0 3px rgba(22, 163, 74, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.target.style.background = '#f9fafb'
                      e.target.style.borderColor = '#d1d5db'
                      e.target.style.boxShadow = 'none'
                    }}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                  />
                  <button
                    type="button"
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      bottom: '0',
                      paddingRight: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#9ca3af',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#6b7280' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af' }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Registration-only fields */}
              {!isLogin && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  paddingTop: '8px',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Vollständiger Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        fontSize: '16px',
                        background: '#f9fafb',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.background = 'white'
                        e.target.style.borderColor = '#16a34a'
                        e.target.style.boxShadow = '0 0 0 3px rgba(22, 163, 74, 0.1)'
                      }}
                      onBlur={(e) => {
                        e.target.style.background = '#f9fafb'
                        e.target.style.borderColor = '#d1d5db'
                        e.target.style.boxShadow = 'none'
                      }}
                      placeholder="Max Mustermann"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </div>

                  {/* Team Field */}
                  <div>
                    <label htmlFor="team" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Team
                    </label>
                    <select
                      id="team"
                      name="team"
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        fontSize: '16px',
                        background: '#f9fafb',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.background = 'white'
                        e.target.style.borderColor = '#16a34a'
                        e.target.style.boxShadow = '0 0 0 3px rgba(22, 163, 74, 0.1)'
                      }}
                      onBlur={(e) => {
                        e.target.style.background = '#f9fafb'
                        e.target.style.borderColor = '#d1d5db'
                        e.target.style.boxShadow = 'none'
                      }}
                      value={formData.team}
                      onChange={(e) => handleInputChange('team', e.target.value)}
                    >
                      {teams.map(team => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </div>

                  {/* Role Field */}
                  <div>
                    <label htmlFor="role" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Rolle
                    </label>
                    <select
                      id="role"
                      name="role"
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        padding: '12px 16px',
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        fontSize: '16px',
                        background: '#f9fafb',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.background = 'white'
                        e.target.style.borderColor = '#16a34a'
                        e.target.style.boxShadow = '0 0 0 3px rgba(22, 163, 74, 0.1)'
                      }}
                      onBlur={(e) => {
                        e.target.style.background = '#f9fafb'
                        e.target.style.borderColor = '#d1d5db'
                        e.target.style.boxShadow = 'none'
                      }}
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value as 'admin' | 'trainer')}
                    >
                      <option value="trainer">Trainer</option>
                      <option value="admin">Administrator</option>
                    </select>
                    <p style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      color: '#6b7280',
                      background: '#fffbeb',
                      border: '1px solid #fed7aa',
                      borderRadius: '12px',
                      padding: '8px'
                    }}>
                      💡 Wählen Sie "Administrator" nur wenn Sie Vollzugriff benötigen
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div style={{ paddingTop: '16px' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  background: isSubmitting 
                    ? '#9ca3af' 
                    : 'linear-gradient(135deg, #1B7E3A 0%, #16a34a 100%)',
                  color: 'white',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: 'none',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 10px 15px -3px rgba(27, 126, 58, 0.3)',
                  transform: 'translateY(0)',
                  opacity: isSubmitting ? '0.5' : '1'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #15803d 0%, #166534 100%)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(27, 126, 58, 0.4)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #1B7E3A 0%, #16a34a 100%)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(27, 126, 58, 0.3)'
                  }
                }}
              >
                {isSubmitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid white',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}></div>
                    <span>Wird verarbeitet...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isLogin ? <LogIn size={18} style={{ marginRight: '8px' }} /> : <UserPlus size={18} style={{ marginRight: '8px' }} />}
                    {isLogin ? 'Anmelden' : 'Registrieren'}
                  </div>
                )}
              </button>
            </div>

            {/* Passwort vergessen — nur im Login-Modus sinnvoll */}
            {isLogin && (
              <div style={{ textAlign: 'center', marginTop: '-8px' }}>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isResetting}
                  style={{
                    color: '#6b7280',
                    background: 'none',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isResetting ? 'default' : 'pointer',
                    textDecoration: 'underline',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => { if (!isResetting) e.currentTarget.style.color = '#16a34a' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280' }}
                >
                  {isResetting ? 'Wird gesendet…' : 'Passwort vergessen?'}
                </button>
              </div>
            )}

            {/* Toggle Login/Register */}
            <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
              <button
                type="button"
                style={{
                  color: '#16a34a',
                  background: 'none',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#15803d' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#16a34a' }}
                onClick={() => {
                  setIsLogin(!isLogin)
                  setError('')
                  setFormData(prev => ({ ...prev, password: '' }))
                }}
              >
                {isLogin 
                  ? 'Noch kein Account? Jetzt registrieren' 
                  : 'Bereits registriert? Hier anmelden'
                }
              </button>
            </div>
          </form>

          {/* Debug Section */}
          <div style={{
            background: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            padding: '16px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '12px'
              }}>
                Probleme beim Anmelden? Browser-Cache könnte das Problem sein.
              </p>
              <button
                type="button"
                onClick={async () => {
                  console.log('🧹 Manual storage clear requested by user')
                  localStorage.clear()
                  sessionStorage.clear()

                  console.log('🍪 Clearing all cookies')
                  document.cookie.split(";").forEach(cookie => {
                    const eqPos = cookie.indexOf("=")
                    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
                  })

                  // Der Knopf hiess "Browser-Cache leeren", raeumte aber nur
                  // Storage und Cookies weg — also gerade NICHT den Cache.
                  // Bei einer PWA sitzt das eigentliche Problem im Service
                  // Worker: er serviert seine vorgecachten Dateien weiter, und
                  // Nutzer bleiben auf einer alten App-Version haengen. Genau
                  // dafuer drueckt man diesen Knopf.
                  try {
                    if ('serviceWorker' in navigator) {
                      const regs = await navigator.serviceWorker.getRegistrations()
                      await Promise.all(regs.map(r => r.unregister()))
                      console.log(`🔄 ${regs.length} Service Worker abgemeldet`)
                    }
                    if ('caches' in window) {
                      const names = await caches.keys()
                      await Promise.all(names.map(n => caches.delete(n)))
                      console.log(`🗑️ ${names.length} Cache-Eintraege geloescht`)
                    }
                  } catch (e) {
                    console.warn('Service Worker / Cache liessen sich nicht vollstaendig leeren:', e)
                  }

                  console.log('✅ Storage, Cookies, Service Worker und Cache geleert')
                  alert('Zwischenspeicher wurde geleert. Die Seite wird jetzt neu geladen.')
                  // true erzwingt in aelteren Engines einen Reload am Cache vorbei;
                  // moderne ignorieren das Argument, der abgemeldete Worker reicht.
                  window.location.reload()
                }}
                style={{
                  fontSize: '12px',
                  padding: '8px 16px',
                  background: 'white',
                  color: '#6b7280',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'white' }}
              >
                🧹 Browser-Cache leeren
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            © 2024 FC Frick - Trainer Management System
          </p>
        </div>
      </div>
    </div>
  )
}
